import { apiClient } from "./api-client";
import type {
  ImportJob,
  UploadResult,
  DetectHeadersResult,
  MappingEntry,
  ValidationResult,
  ImportExecuteResult,
} from "../types/import";

export async function createImportJob(): Promise<ImportJob> {
  return apiClient<ImportJob>("/admin/import-jobs", {
    method: "POST",
    body: JSON.stringify({ type: "csv" }),
  });
}

/**
 * Upload CSV file — uses raw fetch because apiClient forces Content-Type: application/json,
 * but FormData requires the browser to set multipart/form-data with boundary.
 */
export async function uploadImportFile(
  jobId: string,
  file: File,
): Promise<UploadResult> {
  const API_ORIGIN = import.meta.env.VITE_API_URL || "";
  const url = `${API_ORIGIN}/api/v1/admin/import-jobs/${encodeURIComponent(jobId)}/file`;

  const headers: Record<string, string> = {};

  const token = localStorage.getItem("access_token");
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // CSRF token from cookie (same logic as api-client)
  const csrfCookie = document.cookie
    .split(";")
    .find((c) => c.trim().startsWith("quiz-commerce.x-csrf-token="));
  if (csrfCookie) {
    const value = csrfCookie.split("=").slice(1).join("=").trim();
    if (value) headers["X-CSRF-Token"] = decodeURIComponent(value);
  }

  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: formData,
    credentials: "include",
  });

  if (response.status === 401) {
    const { refreshAccessToken } = await import("./auth");
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      const newToken = localStorage.getItem("access_token");
      if (newToken) headers["Authorization"] = `Bearer ${newToken}`;
      const retryResponse = await fetch(url, {
        method: "POST",
        headers,
        body: formData,
        credentials: "include",
      });
      if (retryResponse.ok) {
        const retryBody = (await retryResponse.json()) as {
          success: boolean;
          data: UploadResult;
        };
        return retryBody.data;
      }
    }
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    window.dispatchEvent(new CustomEvent("auth:expired"));
    throw new Error("Sitzung abgelaufen — bitte erneut einloggen.");
  }

  if (!response.ok) {
    const errorBody = (await response.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;
    throw new Error(
      (errorBody.message as string) ||
        (errorBody.error as string) ||
        `Upload fehlgeschlagen (${response.status})`,
    );
  }

  const body = (await response.json()) as {
    success: boolean;
    data: UploadResult;
  };
  return body.data;
}

export async function detectHeaders(
  jobId: string,
): Promise<DetectHeadersResult> {
  return apiClient<DetectHeadersResult>(
    `/admin/import-jobs/${encodeURIComponent(jobId)}/detect-headers`,
    { method: "POST" },
  );
}

export async function saveMapping(
  jobId: string,
  mappings: MappingEntry[],
): Promise<{
  id: string;
  status: string;
  fieldMapping: Record<string, string>;
}> {
  return apiClient(`/admin/import-jobs/${encodeURIComponent(jobId)}/mapping`, {
    method: "POST",
    body: JSON.stringify({ mappings }),
  });
}

export async function validateImport(jobId: string): Promise<ValidationResult> {
  return apiClient<ValidationResult>(
    `/admin/import-jobs/${encodeURIComponent(jobId)}/validate`,
    { method: "POST" },
  );
}

export async function executeImport(
  jobId: string,
): Promise<ImportExecuteResult> {
  return apiClient<ImportExecuteResult>(
    `/admin/import-jobs/${encodeURIComponent(jobId)}/execute`,
    { method: "POST" },
  );
}

/**
 * Reset a stuck job back to 'uploaded' (clears the validation result, keeps
 * parsed rows + field mapping). Mirrors POST /import-jobs/:id/reset.
 */
export async function resetImportJob(jobId: string): Promise<{
  id: string;
  tenantId: string;
  status: string;
  fieldMapping: Record<string, string> | null;
}> {
  return apiClient(
    `/admin/import-jobs/${encodeURIComponent(jobId)}/reset`,
    { method: "POST" },
  );
}

/**
 * Discard a job entirely (hard delete). Mirrors DELETE /import-jobs/:id.
 */
export async function deleteImportJob(
  jobId: string,
): Promise<{ id: string }> {
  return apiClient(`/admin/import-jobs/${encodeURIComponent(jobId)}`, {
    method: "DELETE",
  });
}

export async function downloadErrorCsv(jobId: string): Promise<Blob> {
  const API_ORIGIN = import.meta.env.VITE_API_URL || "";
  const url = `${API_ORIGIN}/api/v1/admin/import-jobs/${encodeURIComponent(jobId)}/errors.csv`;

  const headers: Record<string, string> = {};
  const token = localStorage.getItem("access_token");
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    headers,
    credentials: "include",
  });

  if (response.status === 401) {
    const { refreshAccessToken } = await import("./auth");
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      const newToken = localStorage.getItem("access_token");
      if (newToken) headers["Authorization"] = `Bearer ${newToken}`;
      const retryResponse = await fetch(url, {
        headers,
        credentials: "include",
      });
      if (retryResponse.ok) return retryResponse.blob();
    }
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    window.dispatchEvent(new CustomEvent("auth:expired"));
    throw new Error("Sitzung abgelaufen — bitte erneut einloggen.");
  }

  if (!response.ok) {
    throw new Error(`Download fehlgeschlagen (${response.status})`);
  }

  return response.blob();
}
